/**
 * What ingestion makes from a file, besides storing it.
 *
 * Task 0118. Both partners agreed in `0007`'s Notes that this belongs here
 * rather than in the renderer — @Claude: *"if the renderer has to reach for a
 * full 4GB video to draw a card, no amount of virtualization saves it"*;
 * @Codex: *"the renderer asks for a semantic derivative role (poster,
 * thumbnail, waveform, text extract, preview), not a file format"*. Nothing
 * implemented it. `media.thumb` and `media.poster-frame` were in the
 * vocabulary and in `ROLE_KIND`, and the only `media.poster-frame` in the
 * product was a URL typed into `pools.ts`.
 *
 * ## Why the deriver is a parameter
 *
 * `ingestOne` takes one rather than reaching for a global. Two reasons, and the
 * second is the load-bearing one:
 *
 * - the real deriver needs `createImageBitmap`, a canvas and a `<video>`
 *   element, none of which jsdom has;
 * - decision `0034` wants a control that is a SUBSTITUTION rather than a
 *   deleted assertion. "Ingest the same file with derivation withheld" is
 *   exactly *omit this parameter*, so the control costs nothing to arrange and
 *   cannot drift from the thing it controls.
 *
 * ## What it does NOT do
 *
 * No PDF first page and no audio waveform: both need a decoder the demo does
 * not have. Both are in @Codex's list and both stay in `0118`'s *not in scope*.
 */

import type { BlobId, DerivativeKey } from './deps.js';
import { MAX_DECODED_PIXELS, pictureSize, TooManyPixels } from './image-size.js';
import { PROBE_PNG_BASE64, PROBE_PX } from './encoder-probe-image.js';

/** A picture made from a source blob, under the vocabulary term a card asks for. */
export interface Derivative {
  /** `media.thumb` for a still, `media.poster-frame` for a video. */
  readonly role: 'media.thumb' | 'media.poster-frame';
  readonly bytes: Uint8Array;
  readonly mime: string;
  /** The size it came out at. Free here — both producers compute it through
   *  `fitWithin` on the way to drawing — and decision 0041's index wants it in
   *  the mapping, so a card can be laid out from the index without opening the
   *  picture. */
  readonly width: number;
  readonly height: number;
  /** The source's running time, for a poster frame. Nothing else has one. */
  readonly duration?: number;
}

/** Produce a derivative, or `undefined` if this kind has none. Throws only if
 *  it should have had one and the decode failed — the caller reports that. */
export type Deriver = (source: Uint8Array, mime: string) => Promise<Derivative | undefined>;

/**
 * The long edge of a thumbnail, in CSS pixels before device scaling.
 *
 * Bounded in PIXELS rather than in bytes, which is `0118`'s first clause and
 * not a detail: a byte ceiling on a re-encode is a ceiling on quality, and the
 * thing that makes a card cheap is its dimensions. 320 is twice the widest
 * card the demo draws, so a 2× display still gets a sharp one.
 */
export const THUMB_MAX_PX = 320;

/** WebP everywhere: every browser that can run this build decodes it, and it is
 *  roughly a third of the JPEG for the same picture at this size. */
export const THUMB_MIME = 'image/webp';

/** Named rather than written twice at the two `encodeFrame` call sites, because
 *  it is now part of a KEY: decision 0041 puts encoder quality among the inputs
 *  *"capable of changing the output"*, so a literal that drifts between the two
 *  paths would be two contracts sharing one version number. */
export const THUMB_QUALITY = 0.8;

/**
 * The version of the contract everything above states.
 *
 * Decision 0041: bumped *"whenever decoder choice, frame-selection policy,
 * resize policy, encoder, quality, colour handling, or any other input capable
 * of changing the output changes."* Every one of those is a constant or a line
 * in this file, which is why the number lives here rather than beside the
 * index: the index stores versions, it cannot know what they mean.
 *
 * Concretely, today: `createImageBitmap` / a `<video>` seek to
 * `min(0.1s, duration/2)`, `fitWithin` at `THUMB_MAX_PX` on the long edge and
 * never enlarging, encoded as `THUMB_MIME` at `THUMB_QUALITY`.
 */
export const TRANSFORM_VERSION = 1;

/* ────────────────────────────────────────────────── the encoder's identity */

/**
 * A fixed picture, encoded through the pipeline above, so the key can name what
 * produced the bytes rather than only what was asked for.
 *
 * Task `0129`, from @Codex's P2 on #184: *"When the browser updates its
 * decoder, WebP encoder, orientation handling, or colour pipeline between
 * sessions, this constant and every key parameter remain unchanged even though
 * those implementations can change the derivative bytes and pixels."*
 *
 * `TRANSFORM_VERSION` is a contract number and everything else in decision
 * `0041`'s key is a choice Morph makes. None of them names `createImageBitmap`,
 * `OffscreenCanvas` or `convertToBlob`, and all three are the browser's and all
 * three change under a vault that outlives them. `0041` says *"conflicting
 * completed mappings are integrity errors, not last-writer-wins"* — a rule that
 * assumes the key identifies what produced the value, and could not see two
 * genuinely different encoders writing the same row.
 *
 * A probe is the honest key and it needs no version string the platform does
 * not publish: two encoders that produce identical bytes for this picture are
 * the same encoder for this purpose, and one that does not is a different one.
 *
 * ## Why a picture with noise in it, and why 32×32
 *
 * A flat or smoothly-ramped specimen is one an encoder predicts almost
 * perfectly, so two encoders agree on it while disagreeing about every real
 * photograph — the *specimen could not carry the property* failure this
 * repository has already paid for once. The pattern is a ramp under a
 * deterministic LCG's noise: the ramp gives prediction something to do, the
 * noise gives it something it cannot do, and 32×32 is four WebP macroblocks
 * rather than one, so a per-block mode decision has somewhere to differ.
 *
 * ## What it does NOT cover, said rather than implied
 *
 * The still pipeline only. A poster frame decodes through a `<video>` element,
 * which is a different decoder entirely and one this probe never touches, so a
 * `video-poster-frame` key names its encoder honestly and its decoder not at
 * all. Covering that needs an embedded clip in a codec every target browser
 * opens, which is a larger fixture than this file should carry — recorded in
 * `0129` rather than left for somebody to discover.
 */

/**
 * The digest of the probe's encoded bytes, or `undefined` when this session
 * could not produce one.
 *
 * Memoised for the life of the page, which is the whole point: the encoder does
 * not change under a running tab, and a per-ingest probe would put a decode and
 * an encode on the path decision `0021` budgets. `main.ts` warms it at load so
 * the first drop does not pay for it either.
 *
 * `undefined` rather than a sentinel on failure, and the difference is the
 * whole design: a sentinel is a shared key again, so an unprobed session gets
 * NO key, and `ingestOne` then neither reads nor writes the index. That costs a
 * re-derivation and keeps `0041`'s integrity rule true, which is the right way
 * round — the index is an optimisation and the rule is not.
 */
/**
 * The digest for ONE transform, or `undefined` when this build cannot identify
 * what would run.
 *
 * @Codex, P2 on #184: *"When Chromium or the OS changes its video decoder
 * while the canvas WebP encoder remains stable, every `video-poster-frame` key
 * still receives this hash from `encoderProbe()`, even though that probe only
 * decodes a fixed PNG and never exercises the `<video>` pipeline … tabs with
 * materially different video decoders can still publish conflicting outputs to
 * one key."*
 *
 * `0129` named this gap and left it as prose. That was not enough, and the
 * reason is `0041`'s own rule rather than tidiness: *conflicting completed
 * mappings are integrity errors*, so a key that cannot identify its producer
 * does not merely go stale — it turns a browser update into a reported
 * conflict and a missing preview, on a path whose whole job is to be an
 * invisible optimisation.
 *
 * So `video-poster-frame` gets **no encoder and therefore no key**, which is
 * the second of the two answers `0041` allows and the one this file can
 * actually give today. It costs a re-decode per video ingest — the derivative
 * blob is still content-addressed and still deduplicated, so only the seek
 * repeats — and it keeps the rule true. The first answer, a probe that
 * exercises a `<video>`, needs an embedded clip in a codec every target
 * browser opens; that is a fixture, not a line, and `0129` carries it.
 *
 * Written as a switch on the transform rather than as a caller-side condition
 * so that adding the video probe is one branch here and nothing anywhere else.
 */
/**
 * The formats whose DECODER this build can put a fixture through.
 *
 * @Codex, P2 on `857dbfa`, immediately after the video one: *"When Chromium or
 * the OS changes its JPEG, WebP, GIF, or AVIF decoder while PNG decoding and
 * canvas WebP encoding remain stable, this PNG-only probe keeps returning the
 * same identity for every `image-thumbnail` transform … video indexing is now
 * disabled, but the same PNG digest still covers all five accepted image
 * formats; probe the actual source decoder or omit indexing for formats the
 * probe cannot identify."*
 *
 * Right, and it is the video finding one substitution along — the same gap,
 * inside the transform I had just declared covered. `sourceMime` has been in
 * decision `0041`'s key all along precisely because *"the same bytes offered
 * as `video/mp4` and as `video/webm` are two decodes that may legitimately
 * disagree"*; the probe simply did not vary with it.
 *
 * ## Where the fixtures come from, and why there are only three
 *
 * A probe for format F has to hand the browser a picture IN F. Embedding five
 * of them is five opaque blobs in the source; instead the one embedded PNG is
 * re-encoded through the canvas into the formats the canvas can write, which
 * is exactly `image/png`, `image/jpeg` and `image/webp`. Chromium's canvas
 * writes no GIF and no AVIF, so those two get no fixture, no key and no index
 * — the same second answer `0041` allows and the video transform already
 * takes.
 *
 * That the fixture passes through an encoder as well as a decoder is not a
 * confound: both are the browser's, both are in the pipeline a derivative goes
 * through, and a digest that moves when either moves is what the key wants.
 *
 * The cost is a re-decode per ingest of a GIF or an AVIF. The derivative blob
 * is still content-addressed and deduplicated, so only the decode repeats.
 */
const PROBEABLE = ['image/png', 'image/jpeg', 'image/webp'] as const;

/**
 * And this list is the SHORTCUT, not the guard.
 *
 * Measured rather than assumed: admitting `image/gif` and `image/avif` here
 * changes nothing, because `reEncoded` refuses a canvas that answered with
 * some other type. Remove THAT and the two immediately share PNG's digest —
 * `{"png":"04bd286b0ab8d2bb", "gif":"04bd286b0ab8d2bb", "avif":"04bd28..."}`,
 * which is the defect this whole section is about, reintroduced by the fix for
 * it. The list only saves the work for two formats known not to encode.
 */

const probes = new Map<string, Promise<string | undefined>>();

export function encoderProbe(
  transform: string, sourceMime?: string,
): Promise<string | undefined> {
  if (transform !== 'image-thumbnail') return Promise.resolve(undefined);
  // No source named is not a licence to answer for all of them: the caller
  // that omits it is asking a question this cannot answer.
  if (sourceMime === undefined) return Promise.resolve(undefined);
  if (!(PROBEABLE as readonly string[]).includes(sourceMime)) {
    return Promise.resolve(undefined);
  }
  let held = probes.get(sourceMime);
  if (!held) { held = stillProbe(sourceMime); probes.set(sourceMime, held); }
  return held;
}

/** The still pipeline's own digest for ONE source format: decode a fixture in
 *  that format, draw it, encode the thumbnail, hash the result. */
function stillProbe(sourceMime: string): Promise<string | undefined> {
  return (async () => {
    try {
      const raw = atob(PROBE_PNG_BASE64);
      const png = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i += 1) png[i] = raw.charCodeAt(i);
      /**
       * The fixture IN the source format, made by the browser from the one
       * embedded PNG.
       *
       * For `image/png` this is the PNG itself, so nothing is re-encoded and
       * the digest is the same value it has always been. For the other two the
       * canvas writes the fixture, which means the probe's own bytes move when
       * the encoder moves — and that is wanted: it is the same pipeline the
       * derivative goes through, so a digest that moves when any part of it
       * moves is exactly what the key is for.
       */
      const fixture = sourceMime === 'image/png'
        ? new Blob([png as BlobPart], { type: 'image/png' })
        : await reEncoded(png, sourceMime);
      const decoding = createImageBitmap(fixture);
      // The same late-bitmap leak `thumbnailFromImage` closes, for the same
      // reason: a deadline does not cancel `createImageBitmap`, so a decode
      // that wins the race after we stopped waiting delivers a native bitmap
      // nothing holds a handle to. Registered on the FAILURE path only — a
      // handler attached before the await runs first and detaches the source
      // on the ordinary path, which is how that fix broke every image once.
      const bitmap = await withDeadline(decoding, 'the encoder probe')
        .catch((error: unknown) => {
          void decoding.then((late) => { late.close(); }, () => { /* nothing */ });
          throw error;
        });
      let bytes;
      try {
        bytes = await encodeFrame(
          (ctx, w, h) => { ctx.drawImage(bitmap, 0, 0, w, h); }, PROBE_PX, PROBE_PX);
      } finally {
        // The same leak `thumbnailFromImage` closes: a bitmap holds decoded
        // pixels until something releases it, and a probe that threw during
        // encoding would hold 4 KiB of them for the life of the tab.
        bitmap.close();
      }
      const owned = new Uint8Array(bytes.byteLength);
      owned.set(bytes);
      const hash = await crypto.subtle.digest('SHA-256', owned);
      // Sixteen hex characters, not sixty-four. This value is written into
      // every derivative key and read back on every lookup; 64 bits is far past
      // any chance of two encoders colliding, and the key is a string a person
      // may one day have to read.
      return [...new Uint8Array(hash).slice(0, 8)]
        .map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch {
      return undefined;
    }
  })();
}

/**
 * The probe pattern, written out by the browser in another format.
 *
 * Deliberately NOT `encodeFrame`: that one is the thumbnail encoder and is
 * pinned to `THUMB_MIME` and `THUMB_QUALITY`, which are contract constants a
 * derivative key already records. This writes a FIXTURE, and conflating the
 * two would make a change to thumbnail quality look like a change to the JPEG
 * decoder.
 *
 * Default quality on purpose, for the same reason: naming one here would be a
 * fourth constant nobody decided, and the fixture only has to be stable within
 * a build, not meaningful across builds.
 */
async function reEncoded(png: Uint8Array, mime: string): Promise<Blob> {
  /**
   * BOUNDED, like every other decode in this file — and it was the one that
   * was not.
   *
   * @Codex, P2 on `748dc9a`: *"When the browser's PNG decoder stalls while
   * producing the JPEG or WebP probe fixture, this unbounded
   * `createImageBitmap()` prevents `reEncoded()` and therefore
   * `encoderProbe()` from ever settling; the later `withDeadline()` calls are
   * never reached. Because `main.ts` prewarms only the PNG probe, the first
   * JPEG/WebP ingest encounters this after its source is already durable and
   * then remains stuck without attaching the file or reporting the required
   * missing-preview failure."*
   *
   * Self-introduced, hours earlier, in the commit that added this function:
   * both ENCODE paths below got a deadline and the decode above them did not.
   * The previous fix one dimension short — and the dimension was the one the
   * caller waits on, so the symptom is not a slow ingest but an ingest that
   * never finishes and never fails.
   *
   * The late-bitmap closer is the same one `thumbnailFromImage` and the probe
   * decode carry, for the same reason: a deadline does not cancel
   * `createImageBitmap`, so a decode that wins the race after we stopped
   * waiting delivers a native bitmap nothing holds a handle to. On the FAILURE
   * path only — a handler attached before the await runs first and would close
   * the source on the ordinary path.
   */
  const decoding = createImageBitmap(new Blob([png as BlobPart], { type: 'image/png' }));
  const source = await withDeadline(decoding, 'the probe fixture decode')
    .catch((error: unknown) => {
      void decoding.then((late) => { late.close(); }, () => { /* nothing */ });
      throw error;
    });
  try {
    if (typeof OffscreenCanvas === 'function') {
      const canvas = new OffscreenCanvas(PROBE_PX, PROBE_PX);
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('no 2d context');
      ctx.drawImage(source, 0, 0, PROBE_PX, PROBE_PX);
      const out = await withDeadline(canvas.convertToBlob({ type: mime }), 'the probe fixture');
      // A canvas that cannot write the asked-for type SILENTLY writes a PNG,
      // which would make three formats share one digest again — the whole
      // defect, reintroduced by the fix for it. Refused rather than accepted.
      if (out.type !== mime) throw new Error(`the canvas would not write ${mime}`);
      return out;
    }
    const canvas = document.createElement('canvas');
    canvas.width = PROBE_PX; canvas.height = PROBE_PX;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    ctx.drawImage(source, 0, 0, PROBE_PX, PROBE_PX);
    const out = await withDeadline(new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, mime);
    }), 'the probe fixture');
    if (!out) throw new Error('the canvas would not encode the probe fixture');
    if (out.type !== mime) throw new Error(`the canvas would not write ${mime}`);
    return out;
  } finally {
    source.close();
  }
}

/** The closed operation name for a source of this type, or `undefined` for one
 *  that earns no derivative. NOT the role — decision 0041 keeps the two apart
 *  because two transforms may one day produce one role, and a key that is
 *  spelled with the role could not tell them apart. */
export function transformFor(mime: string): string | undefined {
  const role = derivativeRoleFor(mime);
  if (role === 'media.thumb') return 'image-thumbnail';
  if (role === 'media.poster-frame') return 'video-poster-frame';
  return undefined;
}

/**
 * The key this source and this contract address, or `undefined` when the kind
 * earns no derivative.
 *
 * `sourceMime` is a parameter and not a redundancy: the source blob id is the
 * digest of the BYTES, and the deriver is handed the declared type separately.
 * The same bytes offered as `video/mp4` and as `video/webm` are two decodes
 * that may legitimately disagree, so the declared type is an input capable of
 * changing the output and belongs in the key by decision 0041's own test.
 *
 * `version` is a parameter so a test can substitute one. That is decision
 * 0041's second required control — *"changing only the transform version
 * invokes the decoder and creates a distinct mapping"* — and it cannot be
 * demonstrated at all if the only version in the build is a constant.
 *
 * `encoder` is `encoderProbe()`'s digest, and it is REQUIRED: task `0129`, and
 * an `undefined` here yields no key rather than the old key without it. A
 * defaulted or dropped encoder would be the shared row this exists to end, and
 * `JSON.stringify` drops an `undefined` parameter silently — so the refusal has
 * to happen here, where it is visible, rather than in the encoding.
 *
 * It travels with the DERIVER, not with the build: `derive` is substitutable by
 * decision 0034's control, and a fake deriver's output has nothing to do with
 * the browser's encoder. Naming the build's probe while a fake produced the
 * bytes would be a key that lies in the one way this parameter exists to catch.
 */
export function derivativeKeyFor(
  source: BlobId, mime: string, encoder: string | undefined,
  version: number = TRANSFORM_VERSION,
): DerivativeKey | undefined {
  const transform = transformFor(mime);
  if (!transform || !encoder) return undefined;
  return {
    source,
    transform,
    transformVersion: version,
    parameters: {
      maxPx: THUMB_MAX_PX,
      encoding: THUMB_MIME,
      quality: THUMB_QUALITY,
      sourceMime: mime,
      encoder,
    },
  };
}

/**
 * How long a decode may take before ingestion gives up on it.
 *
 * Task `0112`'s rule, arriving in a second place and for the same reason: a
 * `<video>` that never fires `seeked` — a codec the browser will not open, a
 * truncated file — would otherwise leave ingestion waiting forever, and a
 * surface that has stopped answering is worse than one that says what it could
 * not do. The file still lands; only the picture is missing.
 */
export const DECODE_CEILING_MS = 5_000;

/**
 * The size a picture becomes, preserving its aspect ratio.
 *
 * Never ENLARGES: a 64×64 icon thumbnailed to 320 is three orders of magnitude
 * more bytes for the same picture, which is the opposite of the point.
 */
export function fitWithin(
  width: number, height: number, maxPx = THUMB_MAX_PX,
): { width: number; height: number } {
  if (!(width > 0) || !(height > 0)) return { width: 0, height: 0 };
  const scale = Math.min(1, maxPx / Math.max(width, height));
  // Rounded UP, never to zero: a 1000×3 panorama scaled by 0.32 gives a height
  // of 0.96, and a canvas of height 0 throws rather than drawing nothing.
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** A rejection that names what timed out, so the notice a person reads is not
 *  the word "timeout" on its own. */
export class DecodeTimeout extends Error {
  constructor(what: string, ms: number) {
    super(`${what} did not answer within ${Math.round(ms / 1000)}s`);
    this.name = 'DecodeTimeout';
  }
}

/**
 * `work`, or a `DecodeTimeout` — never neither.
 *
 * The timer is cleared on the settled path, or a 5-second handle is left
 * pending after every successful ingest and the page holds a timer per file for
 * as long as the tab is open.
 */
export async function withDeadline<T>(
  work: Promise<T>, what: string, ms = DECODE_CEILING_MS,
  setTimer: typeof setTimeout = setTimeout,
  clearTimer: typeof clearTimeout = clearTimeout,
): Promise<T> {
  let handle: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      work,
      new Promise<never>((_, reject) => {
        handle = setTimer(() => { reject(new DecodeTimeout(what, ms)); }, ms);
      }),
    ]);
  } finally {
    if (handle !== undefined) clearTimer(handle);
  }
}

/** Which derivative role a source mime earns, or `undefined` for one that earns
 *  none. The one place the mapping lives, so a test and the deriver agree. */
export function derivativeRoleFor(mime: string): Derivative['role'] | undefined {
  if (mime.startsWith('image/')) return 'media.thumb';
  if (mime.startsWith('video/')) return 'media.poster-frame';
  return undefined;
}

/* ─────────────────────────────────────────────── the browser implementation */

/**
 * Draw a source of known size into a canvas at `width`×`height` and encode it.
 *
 * BOTH branches are on the deadline. @Codex, P2 on #184: *"The deadline covers
 * image/video decoding and seeking, but the subsequent asynchronous encoder is
 * awaited without any bound … If the browser's canvas encoder stalls under
 * resource pressure or a codec failure, `ingestOne()` never reaches its
 * derivation catch, so the already durable source is never attached and the UI
 * remains stuck instead of reporting a missing preview."*
 *
 * Task `0112`'s rule again, and this is the third place it has been needed:
 * every unbounded wait in this file is a surface that stops answering, and a
 * surface that has stopped is worse than an error because there is nothing to
 * read. `toBlob` is the sharper case — it resolves through a CALLBACK, so a
 * browser that never calls it leaves a promise nothing can ever settle.
 */
async function encodeFrame(
  draw: (ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    width: number, height: number) => void,
  width: number, height: number,
): Promise<Uint8Array> {
  if (typeof OffscreenCanvas === 'function') {
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    draw(ctx, width, height);
    const blob = await withDeadline(
      canvas.convertToBlob({ type: THUMB_MIME, quality: THUMB_QUALITY }),
      'the image encoder');
    return asThumbMime(blob);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  draw(ctx, width, height);
  const blob = await withDeadline(new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, THUMB_MIME, THUMB_QUALITY);
  }), 'the image encoder');
  if (!blob) throw new Error('the canvas would not encode');
  return asThumbMime(blob);
}

/**
 * The bytes, but only if the canvas actually encoded what was asked for.
 *
 * @Codex, P2 on `9bd1b63`: *"When a browser cannot encode `THUMB_MIME`
 * (`image/webp`), canvas encoding is allowed to return a PNG fallback rather
 * than reject, but both branches here accept that blob without checking
 * `blob.type`. `thumbnailFromImage()` and `posterFromVideo()` then label and
 * store those PNG bytes as `image/webp`, and the probe can even publish an
 * index entry for the mislabeled output."*
 *
 * Exact, and it is specified behaviour rather than a browser quirk: both
 * `toBlob` and `convertToBlob` say an unsupported type falls back to PNG. So
 * the returned bytes are fine and the LABEL is a lie, which is the worse
 * failure of the two — a `morph-blob:<digest>;image/webp` reference selects a
 * row whose bytes are not WebP, and `0041`'s index would publish a mapping
 * naming an encoder that never ran.
 *
 * `reEncoded()` in this same file already guards exactly this for its probe
 * fixtures. Two doors, one of them checked: the shape that has recurred all
 * week here.
 *
 * A refusal rather than a relabel. Storing the PNG under its true type would
 * work and would silently change what `DERIVATIVE_OF` and the transform key
 * mean per browser; an ordinary missing-preview failure is the answer the rest
 * of this file already knows how to report.
 *
 * Exported so the clause can drive it directly. This file's own header says a
 * *"jsdom test of `browserDeriver` would be a test of a stub"* — true, and this
 * is not that: what varies here is the blob a canvas hands back, so the stub is
 * the INPUT rather than the thing under test, and the decision it makes is
 * ordinary logic. Decision `0034`'s question — where does the fact live — puts
 * it here rather than in a browser.
 */
export async function asThumbMime(blob: Blob): Promise<Uint8Array> {
  if (blob.type && blob.type !== THUMB_MIME) {
    throw new Error(`the canvas encoded ${blob.type} rather than ${THUMB_MIME}`);
  }
  return new Uint8Array(await blob.arrayBuffer());
}

async function thumbnailFromImage(source: Uint8Array, mime: string): Promise<Derivative> {
  /**
   * The budget, BEFORE anything allocates. See `image-size.ts` for the
   * arithmetic and for why a decoder option cannot be the bound.
   *
   * An unmeasurable file is now REFUSED rather than decoded, which is the
   * second half of @Codex's P1: *"a highly compressed image can therefore
   * allocate an unbounded bitmap … the new guard treats an unrecognized or
   * mismatched header as safe."* The first version said out loud that it let
   * an unparseable header through, and called that deliberate on the grounds
   * that a parser bug would otherwise become a product refusal.
   *
   * That reasoning was wrong for a reason sitting one line below it: the
   * BROWSER does not trust the declared type either. `createImageBitmap`
   * sniffs, so a PNG renamed `.jpg` decodes anyway and the label was the only
   * thing the budget was reading. `pictureSize` now sniffs too, and what
   * reaches here as `undefined` is a file no parser recognises at all — which
   * a decoder is unlikely to recognise either, and which is exactly the case
   * where an unbounded allocation cannot be ruled out.
   *
   * It costs a preview, never the file: `ingestOne` turns this into a visible
   * missing-preview reason, task 0118 clause 6.
   */
  const intrinsic = pictureSize(source, mime);
  if (!intrinsic) {
    throw new Error(
      `the size of ${mime} could not be read from the file, so a preview would `
      + 'decode an unknown number of pixels',
    );
  }
  if (intrinsic.width * intrinsic.height > MAX_DECODED_PIXELS) {
    throw new TooManyPixels(intrinsic);
  }
  const fit = fitWithin(intrinsic.width, intrinsic.height);
  // The resize is a HINT, not the bound. A decoder that can downsample while
  // decoding will and the budget above never fires; one that decodes in full
  // first is why the budget exists. Both are permitted by the specification.
  /**
   * ONE resize dimension, not both.
   *
   * @Codex, P2 on #184: *"For a JPEG with EXIF orientation that swaps its axes,
   * such as a portrait phone photo stored as 4032×3024 with orientation 6, `fit`
   * is computed from the raw SOF dimensions while `createImageBitmap()` applies
   * orientation by default. Supplying both `resizeWidth` and `resizeHeight`
   * therefore forces the oriented bitmap into the raw landscape ratio and
   * distorts the thumbnail."*
   *
   * A phone photograph held upright is the ordinary case, not the corner. The
   * header parser reads the stored frame and the decoder returns the DISPLAYED
   * one, and nothing here can reconcile them without reading the EXIF
   * orientation tag — which is a second parser for a hint.
   *
   * Giving only the width leaves the decoder to keep its own ratio, so an
   * oriented bitmap comes back undistorted and slightly larger than the box.
   * `fitWithin(bitmap.width, bitmap.height)` below then reduces it to the box
   * for real, which it was already doing. The resize was only ever a hint.
   */
  const decoding = createImageBitmap(new Blob([source as BlobPart], { type: mime }),
    { resizeWidth: fit.width, resizeQuality: 'high' });
  /**
   * A bitmap that arrives AFTER the deadline still has to be closed.
   *
   * @Codex, P2 on #184: *"When `createImageBitmap()` takes longer than five
   * seconds but eventually succeeds, `withDeadline()` rejects first and
   * `thumbnailFromImage()` exits before entering the `try/finally` that calls
   * `bitmap.close()`. The still-running decoder can therefore deliver an
   * unclosed native bitmap after ingestion has already reported failure;
   * repeated slow images can accumulate large decoded surfaces and exhaust the
   * tab despite the deadline."*
   *
   * The `finally` below already argues the point — an `ImageBitmap` holds
   * decoded pixels, a 6000×4000 photo is ~96MB, and the GC will not take it
   * while a reference lives — and then covers only the path where the wait won.
   * A deadline does not cancel the work it stopped waiting for; there is no
   * cancel for `createImageBitmap`. So the timed-out path leaks exactly the
   * thing the deadline was protecting the tab from, which is the failure mode
   * inverted rather than avoided.
   */
  const bitmap = await withDeadline(decoding, 'the image decoder').catch((error: unknown) => {
    // Attached on the FAILURE path only, and the first version was not — it
    // registered the closer before the await, guarded by a flag set after it.
    // A handler registered first on a promise runs first, so on the ordinary
    // path it closed the bitmap before the `await` had even resumed, and every
    // image came back *"The image source is detached"*. Caught by
    // `e2e/derivatives.mjs` going 13/24, which is the whole reason it exists:
    // the leak this fixes needs a five-second decode to show itself and the
    // fix's own defect showed up on every single file.
    void decoding.then((late) => { late.close(); }, () => { /* nothing to close */ });
    throw error;
  });
  try {
    // From the BITMAP, not from the header. If the decoder honoured the resize
    // then `bitmap.width` is already the fit and this is the identity; if it
    // did not, this is the reduction. Reading the header value here instead
    // would draw a resized bitmap at its original size on a decoder that
    // obliged.
    const { width, height } = fitWithin(bitmap.width, bitmap.height);
    const bytes = await encodeFrame((ctx, w, h) => {
      // `as never`: the two context types have identical `drawImage` overloads
      // for a bitmap and TypeScript will not unify them.
      (ctx as CanvasRenderingContext2D).drawImage(bitmap as never, 0, 0, w, h);
    }, width, height);
    return { role: 'media.thumb', bytes, mime: THUMB_MIME, width, height };
  } finally {
    // Released whatever happened. An ImageBitmap holds decoded pixels — a
    // 6000×4000 photo is ~96MB — and the GC will not take it while a reference
    // lives, so a leaked one per ingest is a real ceiling on how many files a
    // session can take.
    bitmap.close();
  }
}

async function posterFromVideo(source: Uint8Array, mime: string): Promise<Derivative> {
  const url = URL.createObjectURL(new Blob([source as BlobPart], { type: mime }));
  const video = document.createElement('video');
  video.muted = true;
  video.preload = 'auto';
  // Or a browser refuses to decode a frame it considers cross-origin, and the
  // canvas is tainted rather than empty — which fails at ENCODE time with a
  // security error rather than at draw time, several steps from the cause.
  video.crossOrigin = 'anonymous';
  try {
    await withDeadline(new Promise<void>((resolve, reject) => {
      video.addEventListener('loadedmetadata', () => { resolve(); }, { once: true });
      video.addEventListener('error', () => {
        reject(new Error(`the browser will not open ${mime}`));
      }, { once: true });
      video.src = url;
    }), 'the video decoder');

    /**
     * The budget BEFORE the seek, not after it.
     *
     * @Codex, P1 on #184: *"For a highly compressed, very high-resolution video
     * that fits under the 64 MiB file ceiling, assigning `currentTime` forces
     * the browser to decode the target frame before the pixel budget is checked
     * … A 16K frame can therefore allocate roughly a gigabyte and freeze or
     * terminate the tab before `TooManyPixels` can protect ingestion."*
     *
     * Right, and it is the still-image finding with the check in the wrong
     * place rather than missing: `loadedmetadata` has already given us
     * `videoWidth` and `videoHeight`, so the guard had everything it needed one
     * statement earlier and was standing after the allocation it was written to
     * prevent. A budget checked after the decode is a comment.
     */
    if (video.videoWidth * video.videoHeight > MAX_DECODED_PIXELS) {
      throw new TooManyPixels({ width: video.videoWidth, height: video.videoHeight });
    }

    // Not frame zero. The first frame of a real recording is very often black
    // or a fade-in, and a poster nobody can recognise is a card with a picture
    // that is worse than no picture. A tenth of a second in, or the midpoint of
    // anything shorter than that.
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    await withDeadline(new Promise<void>((resolve, reject) => {
      video.addEventListener('seeked', () => { resolve(); }, { once: true });
      video.addEventListener('error', () => { reject(new Error('the seek failed')); }, { once: true });
      video.currentTime = duration > 0 ? Math.min(0.1, duration / 2) : 0;
    }), 'the video seek');

    const { width, height } = fitWithin(video.videoWidth, video.videoHeight);
    if (!(width > 0) || !(height > 0)) throw new Error('the video reported no picture size');
    const bytes = await encodeFrame((ctx, w, h) => {
      (ctx as CanvasRenderingContext2D).drawImage(video, 0, 0, w, h);
    }, width, height);
    // Spread rather than `duration: undefined`, because the demo compiles with
    // `exactOptionalPropertyTypes` and an explicitly-undefined optional is not
    // the same as an absent one — which is the right distinction to keep here,
    // since `sameDerivative` treats them as equal and a stored `undefined`
    // would be a field the index carries for no reason.
    return {
      role: 'media.poster-frame', bytes, mime: THUMB_MIME, width, height,
      ...(duration > 0 ? { duration } : {}),
    };
  } finally {
    // Both, and in this order. Revoking while `src` still points at the URL
    // leaves some browsers holding the decoded buffer for the element's life.
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
  }
}

/**
 * The deriver the product uses.
 *
 * Returns `undefined` for a kind with no derivative — audio and PDF today —
 * and THROWS when a kind that should have had one could not be decoded. The
 * difference is the whole of `0118`'s sixth clause: "this file has no thumbnail
 * because audio has none" and "this video would not open" are different things
 * to tell a person, and a single `undefined` cannot say which.
 */
export const browserDeriver: Deriver = async (source, mime) => {
  const role = derivativeRoleFor(mime);
  if (role === 'media.thumb') return thumbnailFromImage(source, mime);
  if (role === 'media.poster-frame') return posterFromVideo(source, mime);
  return undefined;
};
